import React, { createContext, useContext, useState, useEffect } from 'react';

// Define available themes
const themes = {
  dark: {
    name: 'Dark',
    colors: {
      // Backgrounds
      primary: 'bg-gray-900',          // Main page background (dark)
      secondary: 'bg-gray-800',        // Card backgrounds
      tertiary: 'bg-gray-900',         // Header backgrounds
      accent: 'bg-gray-700',           // Secondary elements
      input: 'bg-gray-700',            // Input fields
      
      // Text colors
      textPrimary: 'text-gray-100',    // Main text
      textSecondary: 'text-gray-300',  // Secondary text
      textMuted: 'text-gray-400',      // Muted text
      textInverse: 'text-gray-900',    // Text on light backgrounds
      
      // Borders
      border: 'border-gray-700',       // Primary borders
      borderLight: 'border-gray-600',  // Light borders
      borderDark: 'border-gray-800',   // Dark borders
      
      // Interactive states
      hover: 'hover:bg-gray-700',      // Hover states
      focus: 'focus:ring-gray-500',    // Focus states
      
      // Status colors
      success: 'text-green-400',       // Success states
      warning: 'text-yellow-400',      // Warning states
      error: 'text-red-400',           // Error states
      info: 'text-blue-400',           // Info states
      
      // Buttons
      button: 'bg-gray-600 text-gray-100 hover:bg-gray-700',
      buttonSecondary: 'bg-gray-700 text-gray-100 hover:bg-gray-600',
      buttonDanger: 'bg-red-600 text-gray-100 hover:bg-red-700',
      buttonSuccess: 'bg-green-600 text-gray-100 hover:bg-green-700',
    }
  },
  light: {
    name: 'Light',
    colors: {
      // Backgrounds
      primary: 'bg-gray-50',           // Main page background
      secondary: 'bg-white',           // Card backgrounds
      tertiary: 'bg-gray-100',         // Header backgrounds
      accent: 'bg-gray-200',           // Secondary elements
      input: 'bg-white',               // Input fields
      
      // Text colors
      textPrimary: 'text-gray-900',    // Main text
      textSecondary: 'text-gray-600',  // Secondary text
      textMuted: 'text-gray-500',      // Muted text
      textInverse: 'text-white',       // Text on dark backgrounds
      
      // Borders
      border: 'border-gray-200',       // Primary borders
      borderLight: 'border-gray-300',  // Light borders
      borderDark: 'border-gray-400',   // Dark borders
      
      // Interactive states
      hover: 'hover:bg-gray-50',       // Hover states
      focus: 'focus:ring-blue-500',    // Focus states
      
      // Status colors
      success: 'text-green-600',       // Success states
      warning: 'text-orange-600',      // Warning states (better contrast than yellow)
      error: 'text-red-600',           // Error states
      info: 'text-blue-600',           // Info states
      
      // Buttons
      button: 'bg-blue-600 text-white hover:bg-blue-700',
      buttonSecondary: 'bg-gray-200 text-gray-900 hover:bg-gray-300',
      buttonDanger: 'bg-red-600 text-white hover:bg-red-700',
      buttonSuccess: 'bg-green-600 text-white hover:bg-green-700',
    }
  },
  blue: {
    name: 'Blue',
    colors: {
      // Backgrounds
      primary: 'bg-slate-900',         // Main page background (dark)
      secondary: 'bg-blue-800',        // Card backgrounds
      tertiary: 'bg-blue-900',         // Header backgrounds
      accent: 'bg-blue-700',           // Secondary elements
      input: 'bg-blue-700',            // Input fields
      
      // Text colors
      textPrimary: 'text-blue-50',     // Main text
      textSecondary: 'text-blue-200',  // Secondary text
      textMuted: 'text-blue-300',      // Muted text
      textInverse: 'text-blue-900',    // Text on light backgrounds
      
      // Borders
      border: 'border-blue-600',       // Primary borders
      borderLight: 'border-blue-500',  // Light borders
      borderDark: 'border-blue-700',   // Dark borders
      
      // Interactive states
      hover: 'hover:bg-blue-700',      // Hover states
      focus: 'focus:ring-blue-400',    // Focus states
      
      // Status colors
      success: 'text-green-400',       // Success states
      warning: 'text-yellow-300',      // Warning states (better contrast)
      error: 'text-red-400',           // Error states
      info: 'text-blue-300',           // Info states
      
      // Buttons
      button: 'bg-blue-600 text-blue-50 hover:bg-blue-700',
      buttonSecondary: 'bg-blue-700 text-blue-50 hover:bg-blue-600',
      buttonDanger: 'bg-red-600 text-blue-50 hover:bg-red-700',
      buttonSuccess: 'bg-green-600 text-blue-50 hover:bg-green-700',
    }
  },
  purple: {
    name: 'Purple',
    colors: {
      // Backgrounds
      primary: 'bg-purple-50',         // Main page background
      secondary: 'bg-purple-900',      // Card backgrounds
      tertiary: 'bg-purple-800',       // Header backgrounds
      accent: 'bg-purple-700',         // Secondary elements
      input: 'bg-purple-800',          // Input fields
      
      // Text colors
      textPrimary: 'text-purple-50',   // Main text
      textSecondary: 'text-purple-200', // Secondary text
      textMuted: 'text-purple-300',    // Muted text
      textInverse: 'text-purple-900',  // Text on light backgrounds
      
      // Borders
      border: 'border-purple-600',     // Primary borders
      borderLight: 'border-purple-500', // Light borders
      borderDark: 'border-purple-700', // Dark borders
      
      // Interactive states
      hover: 'hover:bg-purple-700',    // Hover states
      focus: 'focus:ring-purple-400',  // Focus states
      
      // Status colors
      success: 'text-green-400',       // Success states
      warning: 'text-orange-300',      // Warning states (better contrast)
      error: 'text-red-400',           // Error states
      info: 'text-purple-300',         // Info states
      
      // Buttons
      button: 'bg-purple-600 text-purple-50 hover:bg-purple-700',
      buttonSecondary: 'bg-purple-700 text-purple-50 hover:bg-purple-600',
      buttonDanger: 'bg-red-600 text-purple-50 hover:bg-red-700',
      buttonSuccess: 'bg-green-600 text-purple-50 hover:bg-green-700',
    }
  }
};

// Create context
const AdminThemeContext = createContext();

// Theme provider component
export const AdminThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('light');
  
  // Load theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('adminTheme');
    if (savedTheme && themes[savedTheme]) {
      setCurrentTheme(savedTheme);
    }
  }, []);
  
  // Save theme to localStorage when changed
  useEffect(() => {
    localStorage.setItem('adminTheme', currentTheme);
  }, [currentTheme]);
  
  const theme = themes[currentTheme];
  
  const changeTheme = (themeName) => {
    if (themes[themeName]) {
      setCurrentTheme(themeName);
    }
  };
  
  const value = {
    theme,
    currentTheme,
    changeTheme,
    availableThemes: Object.keys(themes).map(key => ({
      key,
      name: themes[key].name
    }))
  };
  
  return (
    <AdminThemeContext.Provider value={value}>
      {children}
    </AdminThemeContext.Provider>
  );
};

// Custom hook to use the theme context
export const useAdminTheme = () => {
  const context = useContext(AdminThemeContext);
  if (!context) {
    throw new Error('useAdminTheme must be used within an AdminThemeProvider');
  }
  return context;
};

// Helper function to get theme classes
export const getThemeClasses = (theme) => {
  return {
    // Layout classes
    page: `min-h-screen ${theme.colors.primary}`,
    header: `${theme.colors.tertiary} shadow-sm border-b ${theme.colors.border}`,
    card: `${theme.colors.secondary} rounded-lg shadow-sm border ${theme.colors.border}`,
    
    // Text classes
    heading: `${theme.colors.textPrimary} font-bold`,
    text: theme.colors.textSecondary,
    muted: theme.colors.textMuted,
    
    // Form classes
    input: `px-3 py-2 border ${theme.colors.borderLight} rounded-lg ${theme.colors.input} ${theme.colors.textPrimary} ${theme.colors.focus} focus:border-transparent`,
    label: `block text-sm font-medium ${theme.colors.textSecondary} mb-2`,
    
    // Button classes
    button: `px-4 py-2 rounded-lg transition flex items-center ${theme.colors.button}`,
    buttonSecondary: `px-4 py-2 rounded-lg transition flex items-center ${theme.colors.buttonSecondary}`,
    buttonDanger: `px-4 py-2 rounded-lg transition flex items-center ${theme.colors.buttonDanger}`,
    buttonSuccess: `px-4 py-2 rounded-lg transition flex items-center ${theme.colors.buttonSuccess}`,
    
    // Interactive classes
    link: `${theme.colors.textMuted} ${theme.colors.hover} transition`,
    iconButton: `p-2 ${theme.colors.textMuted} ${theme.colors.hover} rounded-lg transition`,
    
    // Status classes
    success: theme.colors.success,
    warning: theme.colors.warning,
    error: theme.colors.error,
    info: theme.colors.info,
  };
};

export default AdminThemeContext;